/**
 * 의도-결과 추적 훅
 * 
 * Intent-Outcome 패턴을 위한 공통 훅
 * - intent_id 자동 생성 및 관리
 * - 세션 스토리지를 통한 의도 저장/자동복원
 * - 로그인 게이트 지원 (start 호출 시 자동으로 복원 여부 판단)
 * - latency_ms 자동 계산 (start → success 시간)
 * - parent_intent_id 지원 (하위 의도 연결)
 * 
 * @example
 * ```tsx
 * const intent = useIntent({
 *   type: 'report_request',
 *   requiredGate: 'login',
 *   onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
 *     track('report_request_start', { 
 *       intent_id, 
 *       parent_intent_id,  // 부모 의도 ID (있는 경우)
 *       required_gate,     // 'login' | 'none'
 *       ...context 
 *     });
 *   },
 *   onSuccess: ({ intent_id, context, latency_ms }) => {
 *     track('report_request_success', { 
 *       intent_id, 
 *       latency_ms,  // 자동 계산된 지연 시간 (ms)
 *       ...context 
 *     });
 *   },
 *   onFail: ({ intent_id, context, fail_reason }) => {
 *     track('report_request_fail', { 
 *       intent_id, 
 *       fail_reason,  // 'permission_denied', 'backend_4xx' 등
 *       ...context 
 *     });
 *   },
 * });
 * 
 * // 사용
 * intent.start({ ticker: 'AAPL', market: 'us' }); // 저장된 의도 복원 or 새로 생성
 * 
 * // 부모 의도 연결 (타입으로 자동 조회)
 * loginIntent.start(context, { parentIntentType: 'report_request' });
 * 
 * // 부모 의도 연결 (ID 직접 전달)
 * loginIntent.start(context, { parentIntentId: 'specific_id' });
 * 
 * intent.success();
 * intent.fail('backend_5xx');
 * ```
 */

import { useCallback, useRef } from 'react';
import { generateIntentId, setIntentId, getIntentId, clearIntentId } from '../../../core/ids';

export interface IntentStartParams<TContext = any> {
    intent_id: string;
    context?: TContext;
    parent_intent_id?: string;
    required_gate?: 'login' | 'none';
}

export interface IntentSuccessParams<TContext = any> {
    intent_id: string;
    context?: TContext;
    latency_ms?: number;
}

export interface IntentFailParams<TContext = any> {
    intent_id: string;
    context?: TContext;
    fail_reason?: string;
}

export interface IntentBlockedParams<TContext = any> {
    intent_id: string;
    context?: TContext;
    block_reason?: string;
}

export interface IntentOptions<TContext = any> {
    type: string;  // 의도 타입 (예: 'report_request', 'watchlist_add')
    requiredGate?: 'login' | 'none';  // 필요한 게이트 (없으면 'none')
    onStart?: (params: IntentStartParams<TContext>) => void;
    onSuccess?: (params: IntentSuccessParams<TContext>) => void;
    onFail?: (params: IntentFailParams<TContext>) => void;
    onBlocked?: (params: IntentBlockedParams<TContext>) => void;  //  이벤트 발생 차단
}

export interface IntentActions<TContext = any> {
    start: (
        context?: TContext,
        options?: {
            parentIntentId?: string;      // 부모 의도 ID 직접 전달
            parentIntentType?: string;    // 부모 의도 타입 (자동 조회)
        }
    ) => string;  // 의도 시작 (저장된 의도가 있으면 자동 복원), intent_id 반환
    success: () => void;  // 의도 성공
    fail: (reason?: string) => void;  // 의도 실패
    clear: () => void;  // 의도 정리
    blocked: (reason: string) => void;  // 이벤트 발생 차단
    getIntentId: () => string | null;  // 현재 intent_id 조회
    getContext: () => TContext | null;  // 현재 context 조회
}

/**
 * 의도-결과 추적 훅
 */
export function useIntent<TContext = any>(
    options: IntentOptions<TContext>
): IntentActions<TContext> {
    const { type, requiredGate, onStart, onSuccess, onFail, onBlocked } = options;
    const currentIntentIdRef = useRef<string | null>(null);
    const currentContextRef = useRef<TContext | null>(null);
    const startTimeRef = useRef<number | null>(null);

    /**
     * 의도 시작
     * - 세션 스토리지에 저장된 의도가 있으면 복원 (이벤트 X)
     * - 없으면 새로 시작 (이벤트 O)
     * - parentIntentType 으로 부모 의도 자동 조회
     */
    const start = useCallback((context?: TContext, options?: { parentIntentId?: string; parentIntentType?: string }) => {
        // 1. 먼저 세션 스토리지에 저장된 의도가 있는지 확인
        const stored = getIntentId(type);

        if (stored) {
            // 2. 있으면 복원만 (이벤트는 보내지 않음)
            currentIntentIdRef.current = stored.intentId;
            currentContextRef.current = stored.payload || null;
            return stored.intentId;
        }

        // 3. 없으면 새로 생성
        const intentId = generateIntentId();
        currentIntentIdRef.current = intentId;
        currentContextRef.current = context || null;
        startTimeRef.current = Date.now(); // 시작 시간 기록

        // 부모 의도 ID 결정
        let parentIntentId = options?.parentIntentId;


        // parentIntentType이 주어지면 세션 스토리지에서 자동 조회
        if (options?.parentIntentType && !parentIntentId) {
            const parentStored = getIntentId(options.parentIntentType);
            if (parentStored) {
                parentIntentId = parentStored.intentId;
                // console.log(`🔗 [Intent] Found parent: ${options.parentIntentType} (${parentIntentId})`);
            }
        }

        // 세션 스토리지에 저장 (로그인 게이트 대응)
        setIntentId(type, intentId, context);

        // 시작 이벤트 전송 - track
        if (onStart) {
            onStart({
                intent_id: intentId,
                context,
                parent_intent_id: parentIntentId,
                required_gate: requiredGate,
            });
        }

        // console.log(`🎯 [Intent] Started: ${type} (${intentId})`, context);
        return intentId;
    }, [type, requiredGate, onStart]);

    /**
     * 의도 성공
     */
    const success = useCallback(() => {
        let intentId = currentIntentIdRef.current;
        let context = currentContextRef.current;

        // 메모리에 없으면 세션 스토리지에서 복원 (페이지 리다이렉트 대응)
        if (!intentId) {
            const stored = getIntentId(type);
            if (stored) {
                intentId = stored.intentId;
                context = stored.payload || null;
                currentIntentIdRef.current = intentId;
                currentContextRef.current = context;
                // console.log(`🔄 [Intent] Resumed for success: ${type} (${intentId})`);
            }
        }

        if (!intentId) {
            // console.warn(`⚠️ [Intent] No active intent to mark as success`);
            return;
        }

        // latency_ms 계산
        let latencyMs: number | undefined;
        if (startTimeRef.current) {
            latencyMs = Date.now() - startTimeRef.current;
        }

        // 성공 이벤트 전송
        if (onSuccess) {
            onSuccess({
                intent_id: intentId,
                context: context || undefined,
                latency_ms: latencyMs,
            });
        }

        // console.log(`✅ [Intent] Success: ${type} (${intentId})`, latencyMs ? `${latencyMs}ms` : '');

        // 정리
        clear();
    }, [type, onSuccess]);

    /**
     * 의도 실패
     */
    const fail = useCallback((reason?: string) => {
        let intentId = currentIntentIdRef.current;
        let context = currentContextRef.current;

        // 메모리에 없으면 세션 스토리지에서 복원 (페이지 리다이렉트 대응)
        if (!intentId) {
            const stored = getIntentId(type);
            if (stored) {
                intentId = stored.intentId;
                context = stored.payload || null;
                currentIntentIdRef.current = intentId;
                currentContextRef.current = context;
                // console.log(`🔄 [Intent] Resumed for fail: ${type} (${intentId})`);
            }
        }

        if (!intentId) {
            console.warn(`⚠️ [Intent] No active intent to mark as fail`);
            return;
        }

        // 실패 이벤트 전송
        if (onFail) {
            onFail({
                intent_id: intentId,
                context: context || undefined,
                fail_reason: reason,
            });
        }

        // console.log(`❌ [Intent] Fail: ${type} (${intentId})`, reason);

        // 정리
        // clear();
    }, [type, onFail]);

    /**
     * 이벤트 발생 차단
     */
    const blocked = useCallback((reason: string) => {
        let intentId = currentIntentIdRef.current;
        let context = currentContextRef.current;

        // 메모리에 없으면 세션 스토리지에서 복원 (페이지 리다이렉트 대응)
        if (!intentId) {
            const stored = getIntentId(type);
            if (stored) {
                intentId = stored.intentId;
                context = stored.payload || null;
                currentIntentIdRef.current = intentId;
                currentContextRef.current = context;
                // console.log(`🔄 [Intent] Resumed for blocked: ${type} (${intentId})`);
            }
        }

        if (!intentId) {
            // console.warn('⚠️ [Intent] No active intent to block');
            return;
        }

        if (onBlocked) {
            onBlocked({
                intent_id: intentId,
                context: context || undefined,
                block_reason: reason,
            });
        }

        // console.log(`🚫 [Intent] Blocked: ${type} (${intentId})`, reason);

        // 정리
        clearIntentId(type);
        currentIntentIdRef.current = null;
        currentContextRef.current = null;
    }, [type, onBlocked]);
    /**
     * 의도 정리
     */
    const clear = useCallback(() => {
        clearIntentId(type);
        currentIntentIdRef.current = null;
        currentContextRef.current = null;
        startTimeRef.current = null;
    }, [type]);

    /**
     * 현재 intent_id 조회
     */
    const getIntentIdValue = useCallback(() => {
        return currentIntentIdRef.current;
    }, []);

    /**
     * 현재 context 조회
     */
    const getContext = useCallback(() => {
        return currentContextRef.current;
    }, []);

    return {
        start,
        success,
        fail,
        blocked,
        clear,
        getIntentId: getIntentIdValue,
        getContext,
    };
}

